// src/client/services/authService.ts
//
// Borrower auth: phone-OTP via Supabase Auth. The auth-sms-hook Edge Function
// (already on main) is invoked by Supabase Auth itself — the client never
// calls it directly. SDK responsibilities here:
//   - sendOtp:   normalize phone, call signInWithOtp({ phone, options: { channel: 'sms' } })
//   - verifyOtp: validate code length+digits, call verifyOtp({ phone, token, type: 'sms' })
//   - signOut:   call supabase.auth.signOut()
//
// We do NOT call any Edge Function from this file. Profile-row creation is
// handled by AuthProvider after a successful verify (see Task 11).
import { ok, err, type ServiceResult } from '@/lib/types';
import { OTP_LENGTH, OTP_EXPIRY_SECONDS } from '@/constants/config';
import { isValidZambiaPhone, normalizeZambiaPhone } from '@client/utils/validation';
import { supabase } from '@client/lib/supabase';

export interface SendOtpResult {
  requestId: string;
  expiresInSeconds: number;
}
export interface VerifyOtpResult {
  userId: string;
}

function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `otp-${Date.now()}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

export async function sendOtp(phone: string): Promise<ServiceResult<SendOtpResult>> {
  if (!isValidZambiaPhone(phone)) return err('invalid_phone');
  const normalized = normalizeZambiaPhone(phone);
  if (!normalized) return err('invalid_phone');

  const { error } = await supabase.auth.signInWithOtp({
    phone: normalized,
    options: { channel: 'sms' },
  });
  if (error) {
    const status = (error as { status?: number }).status ?? 0;
    if (status === 429) return err('rate_limited');
    if (status === 400) {
      // Only report invalid_phone when Supabase explicitly flags the number itself.
      // Other 400s (e.g. SMS provider not configured) surface as send_failed so
      // the user sees "Could not send code" rather than the misleading
      // "That number is not valid".
      const text = (error as { message?: string }).message ?? '';
      if (/invalid|not valid/i.test(text)) return err('invalid_phone');
      return err('send_failed');
    }
    return err('send_failed');
  }
  return ok({ requestId: newRequestId(), expiresInSeconds: OTP_EXPIRY_SECONDS });
}

export async function verifyOtp(phone: string, code: string): Promise<ServiceResult<VerifyOtpResult>> {
  if (!isValidZambiaPhone(phone)) return err('invalid_phone');
  if (code.length !== OTP_LENGTH || !/^\d+$/.test(code)) return err('invalid_code');
  const normalized = normalizeZambiaPhone(phone);
  if (!normalized) return err('invalid_phone');

  const { data, error } = await supabase.auth.verifyOtp({
    phone: normalized,
    token: code,
    type: 'sms',
  });
  if (error) {
    const status = (error as { status?: number }).status ?? 0;
    if (status === 401) return err('invalid_code');
    if (status === 429) return err('rate_limited');
    return err('verify_failed');
  }
  const userId = data?.user?.id;
  if (!userId) return err('verify_failed');
  return ok({ userId });
}

export async function signOut(): Promise<ServiceResult<true>> {
  const { error } = await supabase.auth.signOut();
  if (error) return err('signout_failed');
  return ok(true);
}

/**
 * Dev-only: sign in with a real Zambia phone number without SMS OTP.
 * Calls the `dev-login` Edge Function (gated by DEV_LOGIN_ENABLED=true),
 * which creates/finds the user via Admin API and returns a hashed magic-link
 * token. We then verify that token locally to get a real Supabase session.
 *
 * NEVER call this in production. VITE_DEV_LOGIN=true build flag gates the UI.
 */
export async function devLogin(phone: string): Promise<ServiceResult<{ userId: string }>> {
  const normalized = normalizeZambiaPhone(phone);
  if (!normalized) return err('invalid_phone');

  const { data: fnData, error: fnError } = await supabase.functions.invoke('dev-login', {
    body: { phone: normalized },
  });

  if (fnError || typeof fnData?.hashed_token !== 'string') {
    return err('send_failed');
  }

  const { data, error } = await supabase.auth.verifyOtp({
    token_hash: fnData.hashed_token as string,
    type: 'email',
  });

  if (error || !data?.user?.id) return err('verify_failed');
  return ok({ userId: data.user.id });
}
